import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { sanitizeHtml } from "../lib/sanitize";
import { toast } from "sonner";
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  Image as ImageIcon,
  Type,
  Eraser,
  Heading2,
  Heading3,
  Pilcrow,
  Columns2,
  Columns3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
} from "lucide-react";

/**
 * Minimal contentEditable rich-text editor.
 * Stores HTML string. Toolbar appears when the editor is focused.
 *
 * Allowed tags after sanitize: p, br, strong, em, u, h2, h3, a, img, span,
 * div-based column blocks (with class), and inline font/color styles.
 */

const SIZES = [
  { label: "S", value: "0.875rem" },
  { label: "M", value: "1rem" },
  { label: "L", value: "1.25rem" },
  { label: "XL", value: "1.625rem" },
];

function sanitize(html) {
  return sanitizeHtml(html);
}

function stripEditorChrome(html) {
  const template = document.createElement("template");
  template.innerHTML = html || "";
  template.content
    .querySelectorAll(".rt-column-tools, .rt-resize-handle")
    .forEach((node) => node.remove());
  return template.innerHTML;
}

function editableHtml(el) {
  return sanitize(stripEditorChrome(el?.innerHTML || ""));
}

function columnWeights(block) {
  const cols = [...block.querySelectorAll(":scope > .rt-column")];
  return cols.map((col) => {
    const n = Number(col.dataset.width);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
}

function syncColumnLayout(block) {
  const cols = [...block.querySelectorAll(":scope > .rt-column")];
  if (!cols.length) return;
  const weights = columnWeights(block);
  block.dataset.columns = String(cols.length);
  block.classList.remove("rt-columns-2", "rt-columns-3");
  block.classList.add(`rt-columns-${cols.length}`);
  block.style.gridTemplateColumns = weights.map((w) => `${w}fr`).join(" ");

  block.querySelectorAll(":scope > .rt-resize-handle").forEach((node) => node.remove());
  if (cols.length < 2) return;

  const total = weights.reduce((sum, w) => sum + w, 0);
  let acc = 0;
  for (let i = 0; i < cols.length - 1; i += 1) {
    acc += weights[i];
    const handle = document.createElement("button");
    handle.type = "button";
    handle.className = "rt-resize-handle";
    handle.dataset.index = String(i);
    handle.contentEditable = "false";
    handle.setAttribute("aria-label", "Resize columns");
    handle.style.left = `${(acc / total) * 100}%`;
    block.appendChild(handle);
  }
}

export default function RichTextEditor({ value, onChange, placeholder, testId }) {
  const { content } = useContent();
  const ref = useRef(null);
  const savedRange = useRef(null);
  const resizing = useRef(null);
  const onChangeRef = useRef(onChange);
  const [focused, setFocused] = useState(false);
  const [uploading, setUploading] = useState(false);
  const colors = [
    { label: "Default", value: "" },
    { label: "Text", value: content?.theme_text || "#f5f1ea" },
    { label: "Muted", value: content?.theme_muted || "#d8cdb8" },
    { label: "Accent", value: content?.theme_accent || "#c8602c" },
    { label: "Accent hover", value: content?.theme_accent_soft || "#e07a43" },
    { label: "Background", value: content?.theme_bg || "#0a0a0a" },
    { label: "Panel", value: content?.theme_bg_soft || "#171717" },
  ];

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const save = () => {
    if (!ref.current) return;
    onChangeRef.current(editableHtml(ref.current));
  };

  const decorateColumns = () => {
    const el = ref.current;
    if (!el) return;
    el.querySelectorAll(".rt-columns").forEach((block) => {
      const cols = [...block.querySelectorAll(":scope > .rt-column")];
      cols.forEach((col, i) => {
        if (!col.dataset.width) col.dataset.width = "1";
        let tools = col.querySelector(":scope > .rt-column-tools");
        if (!tools) {
          tools = document.createElement("div");
          tools.className = "rt-column-tools";
          tools.contentEditable = "false";
          tools.innerHTML = `<button type="button" class="rt-column-delete" title="Delete column" aria-label="Delete column">×</button>`;
          col.prepend(tools);
        }
        tools.querySelector(".rt-column-delete")?.setAttribute("data-column-index", String(i));
      });
      syncColumnLayout(block);
    });
  };

  // Sync external value changes (e.g. Reset defaults) without losing the caret on each keystroke.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.contains(document.activeElement)) {
      decorateColumns();
      return;
    }
    if (editableHtml(el) !== sanitize(value || "")) {
      el.innerHTML = value || "";
    }
    decorateColumns();
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (ref.current) {
      ref.current.focus();
      save();
      decorateColumns();
    }
  };

  const handleInput = () => {
    if (!ref.current) return;
    saveSelection();
    save();
    decorateColumns();
  };

  const saveSelection = () => {
    const el = ref.current;
    const selection = window.getSelection();
    if (!el || !selection || selection.rangeCount === 0) return;
    const node = selection.anchorNode;
    if (node && el.contains(node)) {
      savedRange.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selection || !savedRange.current) return;
    selection.removeAllRanges();
    selection.addRange(savedRange.current);
  };

  const applyFontSize = (size) => {
    const el = ref.current;
    if (!el || !size) return;
    el.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;

    const span = document.createElement("span");
    span.style.fontSize = size;

    if (range.collapsed) {
      span.appendChild(document.createTextNode("\u200b"));
      range.insertNode(span);
      range.setStart(span.firstChild, 1);
      range.collapse(true);
    } else {
      const fragment = range.extractContents();
      span.appendChild(fragment);
      range.insertNode(span);
      range.selectNodeContents(span);
      range.collapse(false);
    }

    selection.removeAllRanges();
    selection.addRange(range);
    savedRange.current = range.cloneRange();
    save();
    decorateColumns();
  };

  const insertColumns = (count) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    restoreSelection();

    const block = document.createElement("div");
    block.className = `rt-columns rt-columns-${count}`;
    block.setAttribute("data-columns", String(count));

    for (let i = 0; i < count; i += 1) {
      const col = document.createElement("div");
      col.className = "rt-column";
      col.dataset.width = "1";
      col.innerHTML = `<p>Column ${i + 1}</p>`;
      block.appendChild(col);
    }

    const after = document.createElement("p");
    after.innerHTML = "<br>";

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        range.insertNode(after);
        range.insertNode(block);
        range.setStart(after, 0);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        el.appendChild(block);
        el.appendChild(after);
      }
    } else {
      el.appendChild(block);
      el.appendChild(after);
    }

    savedRange.current = null;
    decorateColumns();
    save();
  };

  const deleteColumn = (button) => {
    const col = button.closest(".rt-column");
    const block = col?.closest(".rt-columns");
    if (!col || !block) return;
    const cols = [...block.querySelectorAll(":scope > .rt-column")];
    if (cols.length <= 1) {
      block.remove();
    } else {
      col.remove();
      syncColumnLayout(block);
    }
    save();
    decorateColumns();
    ref.current?.focus();
  };

  const startResize = (handle, event) => {
    const block = handle.closest(".rt-columns");
    if (!block) return;
    const index = Number(handle.dataset.index);
    const cols = [...block.querySelectorAll(":scope > .rt-column")];
    if (!Number.isFinite(index) || !cols[index] || !cols[index + 1]) return;
    resizing.current = {
      block,
      index,
      startX: event.clientX,
      startWidth: block.getBoundingClientRect().width,
      weights: columnWeights(block),
    };
    document.body.classList.add("rt-resizing");
  };

  const handleMouseDown = (e) => {
    const deleteBtn = e.target.closest?.(".rt-column-delete");
    if (deleteBtn) {
      e.preventDefault();
      deleteColumn(deleteBtn);
      return;
    }
    const resizeHandle = e.target.closest?.(".rt-resize-handle");
    if (resizeHandle) {
      e.preventDefault();
      startResize(resizeHandle, e);
      return;
    }
    saveSelection();
  };

  useEffect(() => {
    const onMove = (e) => {
      const state = resizing.current;
      if (!state) return;
      e.preventDefault();
      const { block, index, startX, startWidth, weights } = state;
      const total = weights.reduce((sum, w) => sum + w, 0);
      const delta = ((e.clientX - startX) / Math.max(startWidth, 1)) * total;
      const left = Math.max(0.35, weights[index] + delta);
      const right = Math.max(0.35, weights[index + 1] - delta);
      block.querySelectorAll(":scope > .rt-column").forEach((col, i) => {
        const next = i === index ? left : i === index + 1 ? right : weights[i];
        col.dataset.width = String(Number(next.toFixed(3)));
      });
      syncColumnLayout(block);
    };
    const onUp = () => {
      if (!resizing.current) return;
      resizing.current = null;
      document.body.classList.remove("rt-resizing");
      save();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const handleLink = () => {
    const url = window.prompt("Enter URL (https://…)", "https://");
    if (!url) return;
    exec("createLink", url);
  };

  const handleImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      exec("insertImage", res.data.url);
      toast.success("Image inserted.");
    } catch (e) {
      toast.error("Image upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const Btn = ({ onMouseDown, children, label, active, "data-testid": dti }) => (
    <button
      type="button"
      data-testid={dti}
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown && onMouseDown(e);
      }}
      title={label}
      aria-label={label}
      className={`h-8 w-8 flex items-center justify-center border border-bone-50/10 hover:border-ember hover:text-ember transition-colors ${
        active ? "bg-bone-50/10 text-bone-50" : "text-bone-200/70"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div
      className={`border border-bone-50/10 bg-ink-900 ${
        focused ? "border-ember/60" : ""
      } transition-colors`}
      data-testid={testId}
    >
      {/* Toolbar */}
      <div
        className={`flex flex-wrap items-center gap-1 p-2 border-b border-bone-50/10 ${
          focused ? "opacity-100" : "opacity-70"
        } transition-opacity`}
      >
        <Btn
          onMouseDown={() => exec("bold")}
          label="Bold (⌘B)"
          data-testid={`${testId}-bold`}
        >
          <Bold size={14} />
        </Btn>
        <Btn
          onMouseDown={() => exec("italic")}
          label="Italic (⌘I)"
          data-testid={`${testId}-italic`}
        >
          <Italic size={14} />
        </Btn>
        <Btn
          onMouseDown={() => exec("underline")}
          label="Underline (⌘U)"
          data-testid={`${testId}-underline`}
        >
          <Underline size={14} />
        </Btn>

        <span className="mx-1 h-5 w-px bg-bone-50/10" />

        <Btn
          onMouseDown={() => exec("formatBlock", "<h2>")}
          label="Heading 2"
          data-testid={`${testId}-h2`}
        >
          <Heading2 size={14} />
        </Btn>
        <Btn
          onMouseDown={() => exec("formatBlock", "<h3>")}
          label="Heading 3"
          data-testid={`${testId}-h3`}
        >
          <Heading3 size={14} />
        </Btn>
        <Btn
          onMouseDown={() => exec("formatBlock", "<p>")}
          label="Paragraph"
          data-testid={`${testId}-p`}
        >
          <Pilcrow size={14} />
        </Btn>

        <span className="mx-1 h-5 w-px bg-bone-50/10" />

        <Btn
          onMouseDown={() => exec("justifyLeft")}
          label="Align left"
          data-testid={`${testId}-align-left`}
        >
          <AlignLeft size={14} />
        </Btn>
        <Btn
          onMouseDown={() => exec("justifyCenter")}
          label="Align center"
          data-testid={`${testId}-align-center`}
        >
          <AlignCenter size={14} />
        </Btn>
        <Btn
          onMouseDown={() => exec("justifyRight")}
          label="Align right"
          data-testid={`${testId}-align-right`}
        >
          <AlignRight size={14} />
        </Btn>
        <Btn
          onMouseDown={() => exec("justifyFull")}
          label="Justify"
          data-testid={`${testId}-align-justify`}
        >
          <AlignJustify size={14} />
        </Btn>

        <span className="mx-1 h-5 w-px bg-bone-50/10" />

        {/* Color picker */}
        <div className="flex items-center gap-1">
          <Type size={13} className="text-bone-200/50" />
          {colors.map((c) => (
            <button
              key={c.label}
              type="button"
              data-testid={`${testId}-color-${c.label.toLowerCase()}`}
              title={`Color: ${c.label}`}
              onMouseDown={(e) => {
                e.preventDefault();
                if (c.value) exec("foreColor", c.value);
                else exec("removeFormat");
              }}
              className="h-5 w-5 border border-bone-50/15 hover:scale-110 transition-transform"
              style={{
                background:
                  c.value ||
                  "repeating-linear-gradient(45deg,#444,#444 2px,#222 2px,#222 4px)",
              }}
            />
          ))}
        </div>

        <span className="mx-1 h-5 w-px bg-bone-50/10" />

        {/* Size selector */}
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return;
            applyFontSize(v);
            e.target.value = "";
          }}
          data-testid={`${testId}-size`}
          className="h-8 bg-ink-900 border border-bone-50/10 text-bone-200/80 text-[11px] px-2 hover:border-ember/60"
          defaultValue=""
        >
          <option value="" disabled>
            Size
          </option>
          {SIZES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label} — {s.label === "S" ? "small" : s.label === "M" ? "normal" : s.label === "L" ? "large" : "x-large"}
            </option>
          ))}
        </select>

        <span className="mx-1 h-5 w-px bg-bone-50/10" />

        <Btn onMouseDown={handleLink} label="Insert link" data-testid={`${testId}-link`}>
          <LinkIcon size={14} />
        </Btn>
        <Btn
          onMouseDown={() => insertColumns(2)}
          label="Insert 2 columns"
          data-testid={`${testId}-columns-2`}
        >
          <Columns2 size={14} />
        </Btn>
        <Btn
          onMouseDown={() => insertColumns(3)}
          label="Insert 3 columns"
          data-testid={`${testId}-columns-3`}
        >
          <Columns3 size={14} />
        </Btn>

        <label
          title="Insert image"
          data-testid={`${testId}-image-label`}
          className="h-8 w-8 flex items-center justify-center border border-bone-50/10 text-bone-200/70 hover:border-ember hover:text-ember transition-colors cursor-pointer"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ImageIcon size={14} />
          <input
            type="file"
            accept="image/*"
            data-testid={`${testId}-image-input`}
            className="hidden"
            onChange={(e) => handleImage(e.target.files?.[0])}
          />
        </label>

        <span className="mx-1 h-5 w-px bg-bone-50/10" />

        <Btn
          onMouseDown={() => exec("removeFormat")}
          label="Clear formatting"
          data-testid={`${testId}-clear`}
        >
          <Eraser size={14} />
        </Btn>

        {uploading && (
          <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ember">
            Uploading…
          </span>
        )}
      </div>

      {/* Editor */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onMouseDown={handleMouseDown}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          window.setTimeout(() => {
            if (ref.current?.contains(document.activeElement)) return;
            saveSelection();
            setFocused(false);
          }, 0);
        }}
        data-placeholder={placeholder || "Write something…"}
        data-testid={`${testId}-content`}
        className="rt-editor min-h-[140px] px-4 py-3 text-bone-50 text-sm leading-relaxed focus:outline-none prose-rt"
      />
    </div>
  );
}
